import axios from 'axios';

// Create a base API instance
export const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://modest-quentin-test123as-8496f4e3.koyeb.app',
});

// Add a request interceptor to include token in requests
api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) {
      console.log('Adding auth token to request');
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle errors
api.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    console.error('API Error Response:', error?.response?.status, error?.response?.data);

    // Handle 401 Unauthorized errors globally
    if (error.response && error.response.status === 401) {
      console.log('401 Unauthorized response, clearing token');
      localStorage.removeItem('token');
      // Optionally redirect to login page here if not already there
      if (window.location.pathname !== '/secure-portal-access') {
        window.location.href = '/secure-portal-access';
      }
    }

    return Promise.reject(error);
  }
);

// Authentication API functions
export const login = async (username, password) => {
  try {
    // Use URLSearchParams format for OAuth2 form data
    const formData = new URLSearchParams();
    formData.append('username', username);
    formData.append('password', password);

    const response = await api.post('/token', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    if (response.data.access_token) {
      localStorage.setItem('token', response.data.access_token);
    }

    return response.data;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

export const logout = () => {
  localStorage.removeItem('token');
};

export const getCurrentUser = async () => {
  try {
    console.log('getCurrentUser - making API request');
    const response = await api.get('/users/me');
    console.log('getCurrentUser - success response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error getting current user:', error.response?.data || error.message || error);

    // If unauthorized, clear token
    if (error.response && error.response.status === 401) {
      console.log('getCurrentUser - 401 Unauthorized, clearing token');
      localStorage.removeItem('token');
    }

    throw error;
  }
};

export const updateAdminCredentials = async (data) => {
  try {
    const response = await api.put('/users/update-admin', data);
    return response.data;
  } catch (error) {
    console.error('Error updating admin credentials:', error);
    throw error;
  }
};

// Add better token handling and error logging
export const isAuthenticated = () => {
  const token = localStorage.getItem('token');
  console.log('isAuthenticated check - token exists:', !!token);

  if (!token) {
    return false;
  }

  // You could add token expiry validation here if needed
  return true;
};

// Site settings API functions
export const getSiteSettings = async () => {
  try {
    const response = await api.get('/settings');
    return response.data;
  } catch (error) {
    console.error('Error fetching site settings:', error);
    throw error;
  }
};

export const updateSiteSettings = async (settings) => {
  try {
    const response = await api.put('/settings', settings);
    return response.data;
  } catch (error) {
    console.error('Error updating site settings:', error);
    throw error;
  }
};

// Posts API functions
export const fetchPosts = async (limit = null) => {
  try {
    const params = limit ? { limit } : {};
    const response = await api.get('/posts/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching posts:', error);
    throw error;
  }
};

export const fetchPost = async (id) => {
  try {
    const response = await api.get(`/posts/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching post ${id}:`, error);
    throw error;
  }
};

export const createPost = async (postData) => {
  try {
    // Check if post contains image data (base64)
    if (postData.imageData && postData.imageData.length > 0) {
      // If we have images, we need to handle them appropriately
      // For large payloads, you might consider uploading images separately
      // Here we're sending everything in one request with JSON
      const response = await api.post('/posts/', postData, {
        headers: {
          'Content-Type': 'application/json',
          // Can add additional headers if needed
        },
        // Increase timeout for larger payloads with images
        timeout: 30000
      });
      return response.data;
    } else {
      // Regular post without images
      const response = await api.post('/posts/', postData);
      return response.data;
    }
  } catch (error) {
    console.error('Error creating post:', error);
    throw error;
  }
};

export const updatePost = async (id, postData) => {
  try {
    // Similar handling for image data in updates
    if (postData.imageData && postData.imageData.length > 0) {
      const response = await api.put(`/posts/${id}`, postData, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 30000
      });
      return response.data;
    } else {
      const response = await api.put(`/posts/${id}`, postData);
      return response.data;
    }
  } catch (error) {
    console.error(`Error updating post ${id}:`, error);
    throw error;
  }
};

export const deletePost = async (id) => {
  try {
    const response = await api.delete(`/posts/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting post ${id}:`, error);
    throw error;
  }
};

export const searchPosts = async (query, field = null) => {
  try {
    const params = { query };
    if (field) {
      params.field = field;
    }
    const response = await api.get('/search/', { params });
    return response.data;
  } catch (error) {
    console.error('Error searching posts:', error);
    throw error;
  }
};

export const generateTagSuggestions = async (content) => {
  try {
    // This would typically call an AI/ML service to generate tags based on content
    // For now, this is a placeholder that returns some generic tags
    // In a real application, you might call your backend or a third-party API

    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 500));

    // Basic keyword matching (you'd replace this with actual API call)
    const keywords = {
      'react': ['React', 'JavaScript', 'Frontend'],
      'python': ['Python', 'Programming', 'Backend'],
      'api': ['API', 'Web Development', 'Backend'],
      'database': ['Database', 'SQL', 'Backend'],
      'design': ['Design', 'UI/UX', 'Frontend'],
      'mobile': ['Mobile', 'App Development', 'React Native']
    };

    const lowerContent = content.toLowerCase();
    let suggestedTags = new Set();

    Object.entries(keywords).forEach(([key, tags]) => {
      if (lowerContent.includes(key)) {
        tags.forEach(tag => suggestedTags.add(tag));
      }
    });

    return Array.from(suggestedTags);
  } catch (error) {
    console.error('Error generating tag suggestions:', error);
    return [];
  }
};

// Resources API functions
export const fetchResources = async () => {
  try {
    const response = await api.get('/resources/');
    return response.data;
  } catch (error) {
    console.error('Error fetching resources:', error);
    throw error;
  }
};

export const fetchResourcesByCategory = async (category) => {
  try {
    const response = await api.get(`/resources/category/${category}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching resources for category ${category}:`, error);
    throw error;
  }
};

export const fetchResource = async (id) => {
  try {
    const response = await api.get(`/resources/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching resource ${id}:`, error);
    throw error;
  }
};

export const createResource = async (resourceData) => {
  try {
    // Log what we're sending for debugging
    console.log("Creating resource with data:", {
      title: resourceData.get('title'),
      description: resourceData.get('description'),
      category: resourceData.get('category'),
      type: resourceData.get('type'),
      file: resourceData.get('file'),
      youtube_url: resourceData.get('youtube_url'),
      external_url: resourceData.get('external_url')
    });

    // Send the FormData as is
    const response = await api.post('/resources/', resourceData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      // Increase timeout for larger payloads with images
      timeout: 30000
    });

    return response.data;
  } catch (error) {
    // Enhanced error logging
    console.error('Error creating resource:', error);
    console.error('Error response:', error.response?.data);
    throw error;
  }
};

export const updateResource = async (id, resourceData) => {
  try {
    // Check if resourceData is FormData
    const isFormData = resourceData instanceof FormData;

    const config = {};

    if (isFormData) {
      config.headers = {
        'Content-Type': 'multipart/form-data',
      };
    }

    const response = await api.put(`/resources/${id}`, resourceData, config);
    return response.data;
  } catch (error) {
    console.error(`Error updating resource ${id}:`, error);
    throw error;
  }
};

export const deleteResource = async (id) => {
  try {
    const response = await api.delete(`/resources/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting resource ${id}:`, error);
    throw error;
  }
};

export const getResourceDownloadUrl = (id) => {
  return `${api.defaults.baseURL}/resources/download/${id}`;
};

export default api;